import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import styles from '../../css/admin/NoticeBoard.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import useStoreNo from '../../hooks/useStoreNo';
import translations from '../admin/translations';
import { isApp } from '../../utils/isApp'; // [APP-FORK]
import NoticeDetail from './NoticeDetail';

const PAGE_SIZE = 10;

/** [NEW] 배지 노출 기간(일). */
const NEW_BADGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * 노출 시작일이 최근 7일 이내인지. 값이 없거나 파싱 실패면 false(배지 없음).
 * ★미래 날짜(예약 공지)는 배지 없음 — 하한(diff >= 0)이 없으면 좌변이 음수라
 *  조건이 항상 참이 되어 무기한 NEW 로 보인다.
 */
function isNew(startDttm) {
  if (!startDttm) return false;
  const t = new Date(startDttm).getTime();
  if (Number.isNaN(t)) return false;
  const diff = Date.now() - t;
  return diff >= 0 && diff <= NEW_BADGE_MS;
}

/** timestamptz(ISO) → YYYY.MM.DD. 파싱 실패면 빈 문자열. */
function formatDate(startDttm) {
  if (!startDttm) return '';
  const d = new Date(startDttm);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}`;
}

/**
 * 상점관리자 공지 게시판(/mobile/notice).
 *
 * 팝업(components/common/NoticePopup.js)과는 별개 화면이다 — 팝업은 popup_yn='Y' 인 건만
 * 띄우고, 여기는 use_yn='Y' 인 대상 공지를 전부 목록으로 내려받는다(GET notice/list).
 *
 * [notice-002] 아코디언 → 목록(제목·날짜). ?id=N 이 있으면 같은 화면에서 상세(NoticeDetail.js)를 보인다.
 *  ★상세 번호를 경로가 아닌 쿼리로 두는 이유: 앱은 경로의 첫 숫자를 상점 번호로 읽고, 공지 화면 예외
 *   (상점 헤더 유지)도 경로(/mobile/notice) 정확 일치로 본다 — 쿼리는 둘 다 건드리지 않는다.
 *  ★목록↔상세는 컴포넌트가 바뀌므로 목록의 "더 보기" 누적·스크롤은 상세에서 돌아오면 처음부터다.
 * ★상단 헤더(뒤로가기 + 제목)는 user-app 의 components/Notice/NoticeBoard.tsx 와
 *  같은 형태다. NavBar 는 두지 않는다.
 */
export default function NoticeBoard() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  return id !== null ? <NoticeBoardDetail id={id} /> : <NoticeList />;
}

/**
 * 이전 페이지가 있으면 뒤로, 없으면 fallback 으로.
 * ★'/' 는 홈이 아니라 로그인 화면이다(router.js:40). storeNo 를 복구할 수 있으면
 *  기본 화면으로 보내고, 복구하지 못하면 로그인이 필요한 상태이므로 '/' 가 맞다.
 */
function useBackHandler(fallback) {
  const navigate = useNavigate();
  return useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  }, [navigate, fallback]);
}

/** 목록·상세 공통 골격(페이지 배경·헤더·카드). */
function NoticeBoardFrame({ onBack, children }) {
  const { language } = useLanguage();
  const t = translations[language];
  return (
    <div
      className={`${styles.page} ${styles.noticePage}${
        isApp() ? ` ${styles.pageApp}` : ''
      }`}
    >
      <section className={styles.section}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            aria-label="뒤로"
          >
            ‹
          </button>
          <h1 className={styles.pageTitle}>
            {t.noticeBoardTitle || '공지사항'}
          </h1>
        </div>
        <div className={styles.card}>{children}</div>
      </section>
    </div>
  );
}

/** [notice-002] 상세. 뒤로가기 대체 경로는 목록. */
function NoticeBoardDetail({ id }) {
  const navigate = useNavigate();
  const handleBack = useBackHandler('/mobile/notice');
  return (
    <NoticeBoardFrame onBack={handleBack}>
      <NoticeDetail
        id={id}
        formatDate={formatDate}
        onList={() => navigate('/mobile/notice')}
      />
    </NoticeBoardFrame>
  );
}

function NoticeList() {
  const { fetchData } = useAxiosAdminFetch(); // 로딩의미: api 호출 중
  const navigate = useNavigate();
  // 이 라우트(/mobile/notice)에는 :storeNo 파라미터가 없다 — 훅이 쿼리·localStorage
  //  (persist:root.userInfo.storeNo → last_storeNo) 순으로 복구한다. 없으면 null.
  const storeNo = useStoreNo();
  const { language } = useLanguage(); // 언어 상태 가져오기
  const t = translations[language]; // Translations short-hand

  const [notices, setNotices] = useState([]);
  // 서버 totalPage 와 현재 page 를 비교해 "더 보기" 노출 여부를 정한다.
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  // loading: 첫 페이지 로딩(화면 전체가 "불러오는 중"). 목록이 아직 없다.
  // loadingMore: "더 보기" 로 인한 추가 로딩. 이미 쌓인 목록은 그대로 두고 버튼만 잠근다.
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // error: 첫 페이지 실패 — 목록 자체가 없으므로 화면 전체를 에러 문구로 바꾼다.
  // moreError: 추가 페이지 실패 — 이미 쌓인 목록은 그대로 두고 버튼 아래에만 알린다.
  const [error, setError] = useState(false);
  const [moreError, setMoreError] = useState(false);
  // 같은 page 를 다시 요청하기 위한 카운터(재시도 트리거). 아래 effect 의 의존성이다.
  const [retryTick, setRetryTick] = useState(0);

  // 조회 (Status.js:101-116 호출·언랩 패턴)
  useEffect(() => {
    let cancelled = false;
    // 첫 페이지인지로 두 로딩을 가른다 — page 는 이 effect 의 의존성이라 항상 최신값이다.
    const isFirstPage = page === 1;

    /**
     * 실패 처리. 첫 페이지와 추가 페이지를 가른다 —
     *  추가 페이지에서 setNotices([]) / setError(true) 를 하면 보고 있던 목록이 통째로
     *  사라진다. 누적분은 그대로 두고 버튼 아래에만 알리고, hasMore 는 건드리지 않아
     *  같은 page 를 다시 시도할 수 있게 남긴다.
     */
    const failLoad = () => {
      if (isFirstPage) {
        setNotices([]);
        setHasMore(false);
        setError(true);
      } else {
        setMoreError(true);
      }
    };

    const loadNotices = async () => {
      if (isFirstPage) {
        setLoading(true);
        setError(false);
      } else {
        setLoadingMore(true);
        setMoreError(false);
      }
      try {
        const response = await fetchData({
          method: 'GET',
          url: `${process.env.REACT_APP_API_SERVER}notice/list?pageNo=${page}&pageSize=${PAGE_SIZE}`,
        });
        if (cancelled) return;

        // ResponseInterceptor 로 한 겹 감싸여 온다: { resultCode, resultMessage, data }
        const payload = response?.data?.data;
        if (!payload) {
          // 401 이면 훅(useAxiosAdminFetch)이 이미 로그인 이동을 처리한다.
          failLoad();
          return;
        }
        const list = Array.isArray(payload.list) ? payload.list : [];
        const totalPage = payload.totalPage || 1;
        // 첫 페이지는 교체, 2페이지부터는 뒤에 이어 붙인다.
        setNotices(prev => (isFirstPage ? list : [...prev, ...list]));
        setHasMore(page < totalPage);
      } catch (e) {
        console.error('notice list load error:', e);
        if (cancelled) return;
        failLoad();
      } finally {
        if (!cancelled) {
          if (isFirstPage) setLoading(false);
          else setLoadingMore(false);
        }
      }
    };

    loadNotices();
    return () => {
      cancelled = true;
    };
    // retryTick: 실패한 page 를 그대로 다시 부르기 위한 트리거. page 만 의존성으로 두면
    //  같은 값이라 effect 가 재실행되지 않아 재시도가 아예 동작하지 않는다.
  }, [fetchData, page, retryTick]);

  /**
   * "더 보기". 직전 시도가 실패했으면 page 를 올리지 않는다 —
   *  실패한 그 page 를 다시 불러야 하기 때문이다(올리면 실패한 페이지를 건너뛴다).
   *  대신 moreError 를 해제하고 retryTick 을 올려 effect 를 깨운다.
   */
  const handleMore = useCallback(() => {
    if (moreError) {
      setMoreError(false);
      setRetryTick(prev => prev + 1);
      return;
    }
    setPage(prev => prev + 1);
  }, [moreError]);

  /** 직접 진입(푸시·딥링크)이면 상점관리자 기본 화면으로(router.js:93-96 이 /home 으로 리다이렉트). */
  const handleBack = useBackHandler(
    storeNo ? `/mobile/giftstatus/${storeNo}` : '/'
  );

  return (
    <NoticeBoardFrame onBack={handleBack}>
      {loading ? (
        <div className={styles.empty}>
          {t.noticeBoardLoading || '불러오는 중...'}
        </div>
      ) : error ? (
        <div className={styles.empty}>
          {t.noticeBoardError ||
            '공지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'}
        </div>
      ) : notices.length === 0 ? (
        <div className={styles.empty}>
          {t.noticeBoardEmpty || '등록된 공지가 없습니다.'}
        </div>
      ) : (
        <ul className={styles.list}>
          {notices.map(notice => {
            const dateText = formatDate(notice.startDttm);
            return (
              <li className={styles.item} key={notice.noticeNo}>
                <button
                  type="button"
                  className={styles.itemHeader}
                  onClick={() =>
                    navigate(`/mobile/notice?id=${notice.noticeNo}`)
                  }
                >
                  <span className={styles.itemHeadText}>
                    <span className={styles.itemTitle}>
                      {notice.title}
                      {isNew(notice.startDttm) && (
                        <span className={styles.newBadge}>NEW</span>
                      )}
                    </span>
                    {dateText && (
                      <span className={styles.itemDate}>{dateText}</span>
                    )}
                  </span>
                  <span className={styles.chevron}>›</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* 앱은 Flutter 네이티브 하단 네비가 화면 하단을 점유한다 — 번호식 페이저를 두면
              하단 막대가 두 겹이 되므로 "더 보기"로 이어 붙인다. */}
      {!loading && !error && hasMore && (
        <>
          <button
            type="button"
            className={styles.moreButton}
            onClick={handleMore}
            disabled={loadingMore}
          >
            {loadingMore
              ? t.noticeBoardMoreLoading || '불러오는 중...'
              : t.noticeBoardMore || '더 보기'}
          </button>
          {/* 추가 로딩 실패 — 목록은 그대로 두고 여기에만 알린다. 버튼을 다시 누르면
                  같은 page 로 재시도한다(handleMore 참조). */}
          {moreError && (
            <div className={styles.empty}>
              {t.noticeBoardMoreError ||
                '더 불러오지 못했습니다. 다시 시도해 주세요.'}
            </div>
          )}
        </>
      )}
    </NoticeBoardFrame>
  );
}
